import React, { useEffect, useRef } from "react";
import { Star } from "lucide-react";

const TestimonialsSection = () => {
  const scrollContainerRef = useRef(null);

  const testimonials = [
    {
      name: "Akande Alade",
      role: "Brand Designer of Olax",
      rating: 5,
      text: "Postmint delivered our project with such precision and creativity. Their teams attention to detail and commitment to our vision was outstanding. Highly recommend them!",
    },
    {
      name: "Sarah Johnson",
      role: "CEO of Tech Innovations",
      rating: 5,
      text: "Working with Postmint was a game-changer for our company. They transformed our outdated website into a sleek, modern platform. Highly recommend them!",
    },
    {
      name: "Komalto Deo",
      role: "Director of GoDone Inc.",
      rating: 5,
      text: "I was very satisfied. Professional, efficient, and incredibly creative, they brought our ideas to life exactly as we envisioned. I highly recommend them for their expertise!",
    },
  ];

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let currentIndex = 0;
    let isScrolling = false;

    const scroll = () => {
      if (isScrolling) return;

      const cardWidth =
        container.querySelector(".testimonial-card")?.offsetWidth || 0;
      const gap = 24; // gap-6 = 24px
      const scrollAmount = cardWidth + gap;

      isScrolling = true;
      currentIndex++;

      // Loop back to start after showing all testimonials
      if (currentIndex >= testimonials.length) {
        currentIndex = 0;
      }

      // Scroll to the calculated position
      container.scrollTo({
        left: currentIndex * scrollAmount,
        behavior: "smooth",
      });

      setTimeout(() => {
        isScrolling = false;
      }, 800);
    };

    const interval = setInterval(scroll, 4000);

    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <div className="py-12 sm:py-16 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
            TESTIMONIALS
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-4 px-4">
            What Our Clients Say
          </h2>
          <p className="text-sm sm:text-base text-gray-600 px-4">
            Here's what some of our valued clients have to say about their
            <br className="hidden sm:block" />
            experiences with us.
          </p>
        </div>

        {/* Mobile & Tablet: Auto-scrolling carousel */}
        <div
          ref={scrollContainerRef}
          className="lg:hidden overflow-x-auto scrollbar-hide -mx-4 px-4"
        >
          <div className="flex gap-6 pb-4">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card bg-white p-6 sm:p-8 rounded-2xl shadow-lg flex-shrink-0 w-[85vw] sm:w-[calc(50%-12px)] max-w-md"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-4 h-4 sm:w-5 sm:h-5 fill-blue-400 text-blue-400"
                    />
                  ))}
                </div>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  {testimonial.text}
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
                  <div>
                    <div className="font-bold text-sm sm:text-base text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-500">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Desktop: Grid (3 items) */}
        <div className="hidden lg:grid grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white p-8 rounded-2xl shadow-lg">
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-blue-400 text-blue-400"
                  />
                ))}
              </div>
              <p className="text-gray-600 mb-6">{testimonial.text}</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                <div>
                  <div className="font-bold text-gray-900">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    {testimonial.role}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TestimonialsSection;
